const { ROLE_TYPES, ROLE_CONFIGURATIONS } = require('../constants');

function roleType(input, include) {
  const out = [];
  if (input && input.length) {
    input.forEach((item) => {
      let keep = false;
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const k in item.roles) {
        switch (include) {
          case 'pro':
            if (item.roles[k].role === 'manager' || item.roles[k].role === 'user') {
              keep = true;
            }
            break;
          case 'house':
            if (item.roles[k].role === 'house') {
              keep = true;
            }
            break;
          default:
            break;
        }
      }
      if (keep) {
        out.push(item);
      }
    });
  }
  return out;
}

function prettyRoleType(role = 'UNKNOWN') {
  return ROLE_TYPES.find(({ value }) => value === role)?.name || role;
}

/**
 * @param {array} roles - An array with roles to check (e.g. [{ role: 'admin' }, { role: 'shift', platoonId: null }])
 * @returns {object} - Returns object with suite limitation and configuration
 * @example (returns)
 * {
 *   "envision": {
 *     "read": [],
 *     "write": [],
 *     "limiterIds": [],
 *     "config": {
 *       "name": "Envision",
 *       "read": [ "envision::read"],
 *       "write": ["envision::write::view"],
 *       "providerKey": "enableForecasting"
 *       "roleLabel": function
 *     }
 *   },
 *   ...(every suite + legacy/platoon/management/admin)
 * }
 */
function getRolesBySuites(roles) {
  const defaults = Object.entries(ROLE_CONFIGURATIONS).reduce((result, [key, config]) => ({ ...result, [key]: { read: [], write: [], limiterIds: [], config } }), {});
  
  return roles.reduce((suiteRoles, item) => {
    Object.entries(ROLE_CONFIGURATIONS).forEach(([suite, { limitedBy }]) => {
      if (ROLE_CONFIGURATIONS[suite].read.includes(item.role)) {
        suiteRoles[suite].read.push(item);
        if (limitedBy) {
          if (item[limitedBy] !== undefined) suiteRoles[suite].limiterIds.push(item[limitedBy]); // add limitedBy id
          else suiteRoles[suite].read.pop(); // skip if missing required limitedBy id
        }
      }
      if (ROLE_CONFIGURATIONS[suite].write.includes(item.role)) suiteRoles[suite].write.push(item);
    });
    return suiteRoles;
  }, defaults);
}

/**
 * @param {object} parsedRoles - An object of assorted roles from `getRolesBySuites` (e.g. { admin: { read: [], write: []... }, shift: { read: [], write: []... } })
 * @param {string} suiteKey - An string `key` that references data within parsedRoles (.e.g. 'shift')
 * @returns {string} - Returns string with target suite's access limitation
 * @example (returns)
 * WHEN (some read AND all write) OR `admin` roles exist => 'write'
 * WHEN some read AND some write roles exist => 'limited'
 * WHEN some read roles OR `admin-ro` exist => 'read'
 * ELSE => `null`
 */

function getRoleAccessType(parsedRoles, suiteKey) {
  try {
    const { read, write, config: { write: configWrite } } = (parsedRoles[suiteKey] || { config: {} });
    const isAdmin = parsedRoles?.admin?.write?.length;
    const isAdminRead = isAdmin || parsedRoles?.admin?.read?.length;
    const userWrites = write.map((item) => item.role);
    if (isAdmin || (read.length && configWrite.every((role) => userWrites.includes(role)))) return 'write';
    if (read.length && write.length) return 'limited';
    if (isAdminRead || read.length) return 'read'
  } catch (error) {
    console.error(error);
  }
  return null;
}

/**
 * @param {array} roles - An array with roles to check (e.g. [{ role: 'admin' }, { role: 'shift', platoonId: null }])
 * @param {object} proSettings - An object with provider settings (.e.g. { enableShift: true, enableEnvision: false, ... })
 * @returns {object} - Returns object with suite limitation and configuration
 * @example (returns)
 * {
 *   "envision": {
 *     "type": "envision",
 *     "name": "Envision",
 *     "read": [],
 *     "write": [],
 *     "limiterIds": [],
 *     "isLimited": null,
 *     "access": "No Access",
 *     "limitation": null,
 *     "providerEnabled": true,
 *     "config": {
 *       "name": "Envision",
 *       "read": [ "envision::read"],
 *       "write": ["envision::write::view"],
 *       "providerKey": "enableForecasting",
 *       "roleLabel": function
 *     }
 *   },
 *   ...(every suite + legacy/platoon/management/admin)
 * }
 */
function getRolePermissionOpts(roles, proSettings) {
  const parsedRoles = getRolesBySuites(roles);
  const isAdmin = parsedRoles.admin.write.length;
  const isAdminRead = isAdmin || parsedRoles.admin.read.length;

  return Object.entries(parsedRoles).reduce((result, [key, values]) => {
    const accessType = getRoleAccessType(parsedRoles, key);
    const { limiterIds, config: { name, roleLabel, limitedBy, providerKey } } = values;
    let access = roleLabel();
    let limitation = null;
    let isLimited = null;
    const providerEnabled = providerKey ? Boolean(proSettings[providerKey]) : true;
    
    if (providerEnabled && accessType) {
      if (['write', 'limited'].includes(accessType)) {
        access = roleLabel('write', accessType !== 'write');
      } else if (accessType === 'read') access = roleLabel('read');
      if (accessType && limitedBy) {
        isLimited = !isAdminRead && !isAdmin && !limiterIds.includes(null);
        limitation = isLimited == null ? null : roleLabel('limitation', isLimited);
      }
    }

    return { ...result, [key]: { ...values, type: key, name, access, limitation, isLimited, providerEnabled }};
  }, {});
}

function getDefaultRoles(proSettings) {
  const defaultSuites = ['management', 'shift', 'envision', 'relay'];
  return defaultSuites.reduce((roles, suite) => {
    const config = ROLE_CONFIGURATIONS[suite];
    const providerEnabled = config.providerKey ? Boolean(proSettings[config.providerKey]) : true;
    const parsedRead = config.read.map((role) => config.limitedBy ? ({ role, [config.limitedBy]: null }) : ({ role }));
    return providerEnabled ? roles.concat(parsedRead) : roles;
  }, []);
}

module.exports = {
  roleType: (input, include = 'pro') => roleType(input, include),
  prettyRoleType,
  getDefaultRoles,
  getRolePermissionOpts,
  getRolesBySuites,
  getRoleAccessType,
};
